import React from "react";
import classnames from "classnames";
import dynamic from "next/dynamic";

import { Action, Category } from "@/constants/events";
import { Dispensary, RetailType } from "@/custom-types/Dispensary";
import useDomainCountryCode from "@/hooks/useDomainCountryCode";
import { useEventTracker } from "@/hooks/useEventTracker";
import { useSelector } from "@/redux/hooks";
import {
  getIsOrderingAllowed,
  getIsThirdPartyOrderingProhibited,
} from "@/redux/selectors/complianceRules";
import { getCoordinates } from "@/redux/selectors/location";
import buildDispensaryGoogleMapsUrl from "@/utils/dispensary/buildDispensaryGoogleMapsUrl";
import getDistanceFromDispensary from "@/utils/dispensary/getDistanceFromDispensary";

import StarRating from "@/components/botanic/StarRating";
import Actions from "@/components/Dispensary/DispensaryHeader/Actions";
import BusinessHours from "@/components/Dispensary/DispensaryHeader/BusinessHours";
import DispensaryHeaderFulfillment from "@/components/Dispensary/DispensaryHeaderFulfillment";
import FollowButton from "@/components/Dispensary/FollowDispensaryButton";
import DispensaryPixelTracking from "@/components/DispensaryPixelTracking";
import StorefrontIcon from "@/components/Icons/storefront.svg";
import Image from "@/components/Image";
import LeaflyListBadge from "@/components/LeaflyListBadge";

import ClaimInfoListing from "../ClaimInfoListing";

const UberEgress = dynamic(() => import("@/components/UberEgress"), {
  ssr: true,
});

const NonCompliantMenu = dynamic(
  () => import("@/components/Dispensary/NonCompliantMenu"),
  {
    ssr: true,
  },
);

const PhotoGallery = dynamic(() => import("./PhotoGallery"), {
  ssr: false,
});

const DispensaryHeader: React.FC<{
  dispensary: Dispensary;
  isMainPage?: boolean;
  reviewCount?: number;
}> = ({ dispensary, isMainPage, reviewCount }) => {
  const countryCode = useDomainCountryCode();
  const { publishEvent } = useEventTracker();

  const coordinates = useSelector(getCoordinates);

  const {
    city,
    coverPhotoUrl,
    featureTier,
    flags,
    hasDeliveryEnabled,
    headerTags,
    id,
    logoUrl,
    name,
    pickupEnabled,
    retailType,
    roundedRating,
    slug,
    state,
    uberEatsStoreUrl,
  } = dispensary;

  const googleMapsDirectionsUrl =
    (!hasDeliveryEnabled || pickupEnabled) &&
    buildDispensaryGoogleMapsUrl(dispensary, true);

  const localizedDistance = getDistanceFromDispensary(
    countryCode,
    dispensary,
    coordinates,
  );
  const isInfoTier = featureTier === "info";
  const showPhotoGallery = isMainPage && !isInfoTier && coverPhotoUrl;

  const totalReviewCount = reviewCount || dispensary.reviewCount;

  const thirdPartyOrderingProhibited = useSelector(
    getIsThirdPartyOrderingProhibited,
  );
  const isDispensaryRetailType = retailType === RetailType.Dispensary;
  let isLeaflyOrderingAllowed = useSelector(getIsOrderingAllowed);
  // * Only "dispensary" types are restricted, since clinic and cbd-store types
  // * typically don't sell restricted items.
  if (!isDispensaryRetailType) {
    isLeaflyOrderingAllowed = true;
  }

  return (
    <>
      {state && <DispensaryPixelTracking dispensaryState={state} />}
      <div className="container">
        {showPhotoGallery && (
          <PhotoGallery dispensary={dispensary} className="mb-lg" />
        )}
        <div
          className={classnames("justify-between lg:flex", {
            "pt-xl": !showPhotoGallery,
          })}
        >
          <div className="col lg:col-5 flex flex-col gap-lg relative mb-lg">
            <FollowButton
              className="absolute top-0 right-0"
              dispensaryName={dispensary.name}
              dispensaryId={dispensary.id}
            />
            {!logoUrl ? (
              <div className="h-[50px] w-[50px] flex items-center justify-center bg-leafly-white rounded p-sm">
                <StorefrontIcon
                  className="opacity-20"
                  data-testid="dispensary-header__logo-icon"
                  width="30"
                  height="30"
                />
              </div>
            ) : (
              <Image
                src={logoUrl}
                sizes={[50, null, null, 75, null, 50]}
                alt={`Logo for ${name}`}
                className="rounded overflow-hidden h-[50px] w-[50px] lg:h-[75px] lg:w-[75px]"
                preload
                disableLazyLoad
              />
            )}
            <div className="flex items-start flex-col gap-xs">
              <div className="flex gap-sm flex-wrap">
                {headerTags?.map((tag: string, index: number) => (
                  <div
                    key={index}
                    className="text-xs font-bold border border-light-grey rounded px-sm"
                  >
                    {tag}
                  </div>
                ))}
              </div>
              <h1 className="heading--m">{name}</h1>
              {flags?.includes("leaflyListBadge") && (
                <LeaflyListBadge dispensaryId={id} />
              )}
              <div className="flex">
                <div className="text-xs">{`${city}, ${state}`}</div>
                <span
                  className={`mx-sm ${
                    totalReviewCount > 0 && "after:content-['•']"
                  }`}
                />
                <StarRating
                  rating={roundedRating}
                  ratingCount={totalReviewCount}
                  starLink={
                    isMainPage
                      ? "#reviews-overview"
                      : `/dispensary-info/${slug}/reviews`
                  }
                />
              </div>
              <div className="flex w-full justify-between items-center">
                {googleMapsDirectionsUrl ? (
                  <a
                    className="underline text-xs"
                    href={googleMapsDirectionsUrl}
                    onClick={() => {
                      publishEvent({
                        action: Action.click,
                        category: Category.dispensaryHeader,
                        dispensaryId: id,
                        label: "miles away get directions",
                      });
                    }}
                    rel="noopener noreferrer"
                    target="_blank"
                  >
                    {localizedDistance.full}
                  </a>
                ) : (
                  <div
                    className="text-xs"
                    data-testid="dispensary-header__distance"
                  >
                    {localizedDistance.full}
                  </div>
                )}
                <BusinessHours dispensary={dispensary} />
              </div>
              {isInfoTier && (
                <ClaimInfoListing
                  dispensaryId={id}
                  retailType={dispensary.retailType}
                />
              )}
            </div>
            <Actions dispensary={dispensary} />
          </div>
          <div className="col lg:col-5">
            <DispensaryHeaderFulfillment dispensary={dispensary} />
            {uberEatsStoreUrl && (
              <div className="flex flex-col gap-md py-xs mb-xl">
                <span className="flex self-center text-sm text-grey font-extrabold">
                  – OR –
                </span>
                <UberEgress url={uberEatsStoreUrl} dispensaryId={id} />
              </div>
            )}
          </div>
        </div>
        <div>
          {thirdPartyOrderingProhibited && isDispensaryRetailType && (
            <NonCompliantMenu
              dispensaryId={dispensary.id}
              dispensaryName={dispensary.name}
              dispensaryWebsite={dispensary.website}
              hideCTA={isInfoTier}
              thirdPartyOrderingProhibited={thirdPartyOrderingProhibited}
              isLeaflyOrderingAllowed={isLeaflyOrderingAllowed}
            />
          )}
        </div>
      </div>
    </>
  );
};
export default DispensaryHeader;
